import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from 'src/app/features/auth/services/auth.service';
import { NO_HEADER_ROUTES } from '../../shared/app.constants';
import { AbilityService } from '../services/ability/ability.service';
import { AppConfigService } from '../services/app-config.service';
import { AppService } from '../services/app.service';
import { LogoutService } from '../services/logout.service';
import { SessionService } from '../services/session/session.service';
import { StorageBrowser } from '../services/storage.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private authService: AuthService,
    private storageBrowser: StorageBrowser,
    private appService: AppService,
    private sessionService: SessionService,
    private router: Router,
    private ability: AbilityService,
    private logoutService: LogoutService,
    private appConfig: AppConfigService,
  ) {
    console.log('[AuthInterceptor] Initialized');
  }

  private isLoginRoute(): boolean {
    return this.router.url.startsWith('/auth/login');
  }

  private isPublicRoute(url: string): boolean {
    return NO_HEADER_ROUTES.some(route => url.includes(route));
  }

  private isWriteOperation(method: string): boolean {
    return ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase());
  }

  private isInMonitorMode(): boolean {
    const monitorToken = localStorage.getItem('monitor_token');
    return !!monitorToken;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for public routes
    if (this.isPublicRoute(request.url)) {
      console.log('[AuthInterceptor] Skipping public route:', request.url);
      return next.handle(request);
    }
    if (request.headers.has('skip-auth')) {
      const cleanReq = request.clone({
        headers: request.headers.delete('skip-auth'),
      });
      return next.handle(cleanReq);
    }



    // Use monitor token if in monitor mode, otherwise use regular token
    const token = this.isInMonitorMode()
      ? localStorage.getItem('monitor_token')
      : this.storageBrowser.get('token');

    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(request).pipe(
      catchError((error) => {
        const isReadOnlyAttempt =  error.error?.message === 'AUTHORIZATION.READ_ONLY_TOKEN'
        console.error('[AuthInterceptor] HTTP Error:', {
          status: error.status,
          url: request.url,
          isReadOnlyAttempt
        });

        // Handle read-only write attempt - trigger step-up modal
        if (
          error instanceof HttpErrorResponse &&
          isReadOnlyAttempt
        ) {
          console.log('[AuthInterceptor] 403 Read-only write attempt - triggering step-up');
          this.sessionService.showStepUpModal();
          return throwError(() => error);
        }

        // Handle 401 Unauthorized
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !request.url.includes('login') &&
          !request.url.includes('refresh-token')
        ) {
          console.log('[AuthInterceptor] 401 Detected - Attempting token refresh...');
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.getFreshTokenCoordinated(request)).pipe(
        switchMap((newToken: string | null) => {
          this.isRefreshing = false;

          if (!newToken) {
            return throwError(() => new Error('Token refresh returned no token'));
          }

          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;

          // SSO-only lab: never fall back to the local /auth/login (there is
          // no password to enter). Hand off to sso-fe's Labs picker instead.
          if (this.appConfig.isSsoEnabled()) {
            const base = this.appConfig.getSsoFrontendUrl();
            this.logoutService.logoutAndRedirect(`${base}/labs`);
            return throwError(() => err);
          }

          if (this.isLoginRoute()) {
            this.storageBrowser.remove('user');
            this.storageBrowser.remove('token');
            this.storageBrowser.remove('monitor_token');
            this.storageBrowser.remove('last_active');
            return throwError(() => err);
          }

          // SSO sessions: refresh failure (e.g. revoked token) → kick to
          // main lab rather than dropping on the sub-lab's /auth/login
          // (where the SSO physician likely has no password).
          if (this.ability.isSSO && this.ability.mainLabUrl) {
            this.logoutService.logoutAndRedirect(this.ability.mainLabUrl);
            return throwError(() => err);
          }
          this.appService.logout('Session expired. Please login again.');
          return throwError(() => err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      switchMap((jwt) => next.handle(this.addToken(request, jwt)))
    );
  }

  /**
   * Get a valid access token to retry a 401'd request, coordinated across tabs.
   *
   * The backend rotates the refresh token on every refresh with no grace window,
   * so concurrent refreshes from multiple tabs would make one revoke the other
   * ("Refresh Token Invalid or Revoked"). We serialize with the proactive
   * refresh and other tabs' reactive refreshes via the shared Web Lock, and —
   * once inside the lock — reuse a token that another tab (or the proactive
   * timer) already rotated, detected by comparing the failed request's token to
   * the current stored one, instead of refreshing again. Net effect: any number
   * of simultaneous multi-tab 401s trigger exactly one network refresh; everyone
   * else reuses the rotated token. See .
   */
  private getFreshTokenCoordinated(
    request: HttpRequest<any>,
  ): Promise<string | null> {
    const failedToken =
      request.headers.get('Authorization')?.replace(/^Bearer\s+/, '') ?? null;

    const run = async (): Promise<string | null> => {
      // Another tab / the proactive timer already refreshed since this request
      // was sent → its token is valid; reuse it instead of racing a refresh.
      const current = this.storageBrowser.get('token');
      if (current && current !== failedToken) {
        return current;
      }

      const res: any = await firstValueFrom(
        this.authService.refreshToken().pipe(timeout(10000)),
      );
      const newToken = res?.token ?? null;
      if (newToken) {
        this.storageBrowser.set('token', newToken);
        // Stamp the shared cross-tab refresh time so other tabs' proactive
        // refresh ticks skip and don't trigger a rotation race.
        try {
          localStorage.setItem('last_token_refresh_at', Date.now().toString());
        } catch {}
      }
      return newToken;
    };

    const locks: any = (navigator as any).locks;
    if (!locks?.request) {
      // No Web Locks support → in-tab single-flight (AuthService) + the token
      // reuse check still cover most races.
      return run();
    }

    // Exclusive + queued: wait for any in-flight refresh (this or another tab)
    // to finish, then run(). A safety-timeout signal prevents a stuck lock from
    // hanging the request; browsers also auto-release locks held by dead tabs.
    const signal =
      typeof (AbortSignal as any)?.timeout === 'function'
        ? (AbortSignal as any).timeout(15000)
        : undefined;

    return locks
      .request('dormotech_token_refresh', signal ? { signal } : {}, run)
      .catch((err: any) => {
        // Only the safety-timeout aborting a still-*pending* lock acquisition
        // produces an AbortError (the callback never ran) → fall back to a
        // direct refresh. Any other error is a real refresh failure from run()
        // and must propagate so we don't refresh twice.
        if (err?.name === 'AbortError') {
          return run();
        }
        throw err;
      });
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
